import { memo, useSyncExternalStore } from 'react';

import {
  getFruitNinjaRemoteUiSnapshot,
  subscribeFruitNinjaRemoteUi,
} from '../../state/fruitNinjaRemoteUiStore.ts';

import { FRUIT_NINJA_REMOTE_DOM } from './fruitNinjaRemoteDomIds.ts';

const MODE_LABELS: Record<'CLASSIC' | 'ARCADE' | 'ZEN', string> = {
  CLASSIC: 'Classic',
  ARCADE: 'Arcade',
  ZEN: 'Zen',
};

const MAX_CLASSIC_LIVES = 3;

/** Visual scale left → right (reference: smallest … largest). */
const LIFE_SLOT_SCALE: readonly number[] = [0.88, 0.98, 1.1];

/**
 * Filled heart (Heroicons solid). Red = life; dark + cyan halo = lost slot.
 */
const LifeHeartIcon = memo(function LifeHeartIcon({
  hasLife,
  sizeIndex,
}: {
  hasLife: boolean;
  sizeIndex: number;
}) {
  const s = LIFE_SLOT_SCALE[sizeIndex] ?? 1;
  return (
    <span
      className="fruit-remote-ui__life-slot"
      style={{ transform: `scale(${s})` }}
      aria-hidden
    >
      <svg
        className={
          hasLife
            ? 'fruit-remote-ui__life-svg fruit-remote-ui__life-svg--alive'
            : 'fruit-remote-ui__life-svg fruit-remote-ui__life-svg--empty'
        }
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden
        focusable="false"
      >
        <path
          fillRule="evenodd"
          d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 0 1-.383-.218 25.18 25.18 0 0 1-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0 1 12 5.052 5.5 5.5 0 0 1 16.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 0 1-4.244 3.17l-.022.012-.007.003-.002.001h-.002z"
          clipRule="evenodd"
        />
      </svg>
    </span>
  );
});

function classicLifeXs(lives: number) {
  const L = Math.min(MAX_CLASSIC_LIVES, Math.max(0, lives));
  const lost = MAX_CLASSIC_LIVES - L;
  return Array.from({ length: MAX_CLASSIC_LIVES }, (_, i) => (
    <LifeHeartIcon key={i} hasLife={i >= lost} sizeIndex={i} />
  ));
}

function PlayerScoreBlock({
  label,
  score,
  lives,
  streak,
  align,
}: {
  label: string;
  score: number | null;
  lives: number | null;
  streak: number | null;
  align: 'left' | 'right';
}) {
  const showLives = lives !== null;
  return (
    <div className={`fruit-remote-ui__player-block fruit-remote-ui__player-block--${align}`}>
      <div className="fruit-remote-ui__player-label">{label}</div>
      <div className="fruit-remote-ui__score-row">
        <img
          className="fruit-remote-ui__watermelon"
          src="/media/watermelon_half.png"
          alt=""
          width={130}
          height={100}
          decoding="async"
          loading="lazy"
        />
        <span className="fruit-remote-ui__score">{score ?? 0}</span>
      </div>
      <div className="fruit-remote-ui__player-streak">{(streak ?? 0) > 0 ? `BEST: ${streak}` : ''}</div>
      {showLives && (
        <div className="fruit-remote-ui__lives" role="img">
          {classicLifeXs(lives)}
        </div>
      )}
    </div>
  );
}

export function FruitNinjaRemoteHud() {
  const d = FRUIT_NINJA_REMOTE_DOM;
  const s = useSyncExternalStore(
    subscribeFruitNinjaRemoteUi,
    getFruitNinjaRemoteUiSnapshot,
    getFruitNinjaRemoteUiSnapshot,
  );

  const timerText = s.timeLeftSec !== null && s.timeLeftSec !== undefined ? `⏱ ${s.timeLeftSec}s` : '';

  /** Native often omits `lives` in Classic; treat as full until a number arrives. */
  const livesForHud =
    s.gameMode === 'CLASSIC' ? (s.lives ?? MAX_CLASSIC_LIVES) : s.lives;
  const showLives = livesForHud !== null;
  const livesAria = showLives ? `Lives: ${livesForHud} of ${MAX_CLASSIC_LIVES}` : undefined;

  return (
    <div id={d.root} className="fruit-remote-ui" hidden={!s.hudVisible}>
      <header className="fruit-remote-ui__header" aria-label="Score and timer">
        {s.split ? (
          <>
            <PlayerScoreBlock
              label="P1"
              score={s.player1Score}
              lives={s.player1Lives}
              streak={s.player1Streak}
              align="left"
            />
            <div className="fruit-remote-ui__header-center">
              <div
                className={
                  s.timerUrgent
                    ? 'fruit-remote-ui__timer fruit-remote-ui__timer--urgent'
                    : 'fruit-remote-ui__timer'
                }
              >
                {timerText}
              </div>
            </div>
            <PlayerScoreBlock
              label="P2"
              score={s.player2Score}
              lives={s.player2Lives}
              streak={s.player2Streak}
              align="right"
            />
          </>
        ) : (
          <>
            <div className="fruit-remote-ui__header-left">
              <div className="fruit-remote-ui__score-row">
                <img
                  className="fruit-remote-ui__watermelon"
                  src="/media/watermelon_half.png"
                  alt=""
                  width={130}
                  height={100}
                  decoding="async"
                  loading="lazy"
                />
                <div className="fruit-remote-ui__score-stack">
                  <span className="fruit-remote-ui__score">{s.score}</span>
                </div>
              </div>
              <div> {s.streak !== null && (
                <span className="fruit-remote-ui__best">
                  BEST: {s.streak}
                </span>
              )}</div>
            </div>
            <div className="fruit-remote-ui__header-center">
              <div
                className={
                  s.timerUrgent
                    ? 'fruit-remote-ui__timer fruit-remote-ui__timer--urgent'
                    : 'fruit-remote-ui__timer'
                }
              >
                {timerText}
              </div>
            </div>
            <div className="fruit-remote-ui__header-right">
              <div
                className={
                  showLives ? 'fruit-remote-ui__lives' : 'fruit-remote-ui__lives fruit-remote-ui__lives--hidden'
                }
                role="img"
                aria-label={livesAria}
              >
                {showLives ? classicLifeXs(livesForHud) : null}
              </div>
            </div>
          </>
        )}
      </header>
      {/* <footer className="fruit-remote-ui__footer" aria-label="Mode and combo">
        <span className="fruit-remote-ui__mode">
          {s.gameMode !== null ? MODE_LABELS[s.gameMode] ?? s.gameMode : ''}
        </span>
        <span className="fruit-remote-ui__streak">{!s.split && s.streak > 0 ? `🔥 ×${s.streak}` : ''}</span>
      </footer> */}
    </div>
  );
}
